import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { DepartmentDisplay } from '@modules/department/models/department-display';
import { Store } from '@ngrx/store';
import { LicenseState, supportAccessLicenseStates } from '@pixacare/domain';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { AvatarStyleService } from 'src/app/services/avatar-style.service';
import { MenuEntry } from 'src/app/shared/models/base/menu-entry';
import { selectUserActiveLicenseState } from 'src/app/shared/store/licenses/licenses.selectors';
import { ChatService } from '../../chat/chat.service';
import { SidebarService } from './sidebar.service';

const clientCodeReplacementToken = '#{clientCode}#';
const departmentIdReplacementToken = '#{departmentId}#';

@Injectable()
export class MenuBuilderService {
  private authService = inject(AuthenticationService);
  private readonly avatarStyleService = inject(AvatarStyleService);
  private readonly store = inject(Store);
  private readonly sidebarService = inject(SidebarService);

  private readonly chatService = inject(ChatService);
  private readonly translocoService = inject(TranslocoService);

  clientMenuEntries$: Observable<MenuEntry[]>;
  departmentsEntries$: Observable<MenuEntry[]>;

  // Conditions

  private readonly isLicenseSupport = ({ licenseState }: { licenseState: LicenseState }): boolean =>
    licenseState === LicenseState.SUPPORT;

  private readonly isDepartmentOpen = ({ department }: { department: DepartmentDisplay }): boolean => department.isOpen;

  private readonly isUserAdmin = ({ department }: { department: DepartmentDisplay }): boolean =>
    department.memberships?.[this.authService.user.id]?.isAdmin;

  // Entries

  readonly userEntries: MenuEntry[] = [
    {
      title: this.translocoService.translate('Messagerie'),
      testId: 'menu-chat',
      icon: '@tui.message-square',
      link: '/chat',
      count: this.chatService.unreadMessagesCounter$,
    },
    {
      title: this.translocoService.translate('Mes télésuivis'),
      testId: 'menu-telemonitorings',
      icon: 'telemonitoring',
      link: '/telemonitorings',
      queryParams: {
        is_archived: 'false',
      },
    },
    {
      title: this.translocoService.translate('Mes mots clés'),
      testId: 'menu-labels',
      icon: '@tui.tag',
      link: '/labels',
    },
    {
      title: this.translocoService.translate('Partagé avec moi'),
      testId: 'menu-shared-to-me',
      icon: '@tui.share-2',
      link: '/sharedtome',
    },
  ];

  private readonly clientEntries: MenuEntry[] = [
    {
      title: this.translocoService.translate('Administration'),
      testId: 'menu-client-admin',
      icon: '@tui.settings',
      link: '/client/#{clientCode}#/admin',
      condition: ({ licenseState }) => supportAccessLicenseStates.includes(licenseState),
    },
    {
      title: this.translocoService.translate('Ma galerie'),
      testId: 'menu-client-gallery',
      icon: '@tui.image',
      link: '/client/#{clientCode}#/sequences',
      condition: ({ licenseState }) => !this.isLicenseSupport({ licenseState }),
    },
    {
      title: this.translocoService.translate('Mes patients'),
      testId: 'menu-client-patients',
      icon: '@tui.users',
      link: '/client/#{clientCode}#/patients',
      queryParams: {
        is_archived: 'false',
      },
      condition: ({ licenseState }) => !this.isLicenseSupport({ licenseState }),
    },
  ];

  private readonly departmentEntries: MenuEntry[] = [
    {
      title: this.translocoService.translate('Administration'),
      testId: 'menu-department-admin',
      icon: '@tui.settings',
      link: '/client/#{clientCode}#/department/#{departmentId}#/admin',
      condition: ({ licenseState, department }) =>
        this.isUserAdmin({ department }) || this.isLicenseSupport({ licenseState }),
    },
    {
      title: this.translocoService.translate('Galerie du groupe'),
      testId: 'menu-department-gallery',
      icon: '@tui.image',
      link: '/client/#{clientCode}#/department/#{departmentId}#/sequences',
      condition: ({ licenseState, department }) =>
        (this.isDepartmentOpen({ department }) || this.isUserAdmin({ department })) &&
        !this.isLicenseSupport({ licenseState }),
    },
    {
      title: this.translocoService.translate('Patients du groupe'),
      testId: 'menu-department-patients',
      icon: '@tui.users',
      link: '/client/#{clientCode}#/department/#{departmentId}#/patients',
      queryParams: {
        is_archived: 'false',
      },
      condition: ({ licenseState, department }) =>
        (this.isDepartmentOpen({ department }) || this.isUserAdmin({ department })) &&
        !this.isLicenseSupport({ licenseState }),
    },
  ];

  constructor() {
    this.clientMenuEntries$ = this.computeMenuEntries(this.clientEntries);

    this.departmentsEntries$ = this.sidebarService.departments$.pipe(
      // Get entries for each departments
      switchMap((departments) =>
        departments?.length > 0
          ? combineLatest(
              departments.map((department) =>
                this.computeMenuEntries(this.departmentEntries, department).pipe(
                  map((entries) => ({ department, entries })),
                ),
              ),
            )
          : of([]),
      ),
      // Wrap them in their folder
      map((departmentsWithEntries) =>
        departmentsWithEntries
          .filter(({ entries }) => entries.length > 0)
          .map(({ department, entries }) => ({
            id: department.id,
            title: department.name,
            style: this.avatarStyleService.getAvatarStyle(department.name),
            children: entries,
          })),
      ),
    );
  }

  private computeMenuEntries(entries: MenuEntry[], department?: DepartmentDisplay): Observable<MenuEntry[]> {
    return combineLatest([this.sidebarService.activeClientCode$, this.store.select(selectUserActiveLicenseState)]).pipe(
      map(([clientCode, licenseState]) => ({
        validEntries: entries.filter((entry) => !entry.condition || entry.condition({ licenseState, department })),
        clientCode,
      })),
      map(({ validEntries, clientCode }) =>
        this.resolveTokens(validEntries, { clientCode, departmentId: department?.id }),
      ),
    );
  }

  private resolveTokens(
    entries: MenuEntry[],
    values: {
      clientCode: string;
      departmentId?: number;
    },
  ): MenuEntry[] {
    return entries.map((entry) => ({
      ...entry,
      link: this.resolveString(entry.link, values),
      queryParams:
        entry.queryParams &&
        Object.entries(entry.queryParams).reduce(
          (queryParams, [key, queryParam]) => ({
            ...queryParams,
            [key]: this.resolveString(queryParam, values),
          }),
          {},
        ),
    }));
  }

  private resolveString(
    value: string,
    {
      clientCode,
      departmentId,
    }: {
      clientCode: string;
      departmentId?: number;
    },
  ): string {
    const result = value.replace(clientCodeReplacementToken, clientCode);

    if (departmentId) {
      return result.replace(departmentIdReplacementToken, departmentId.toString());
    }

    return result;
  }
}
